import { useState, useMemo, useCallback, useEffect, useRef, type FocusEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { type Dayjs } from "dayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { type DateRange, type DateRangeValidationError } from "@mui/x-date-pickers-pro/models";
import { type PickersShortcutsItem, type PickersShortcutsProps } from "@mui/x-date-pickers/PickersShortcuts";
import { type PickerChangeHandlerContext } from "@mui/x-date-pickers/models";
import { usePickerContext } from "@mui/x-date-pickers/hooks";
import { type PickerRangeValue } from "@mui/x-date-pickers/internals";
import { PickersTextField } from "@mui/x-date-pickers/PickersTextField";
import { List, ListItemButton, ListItemText, styled, type SxProps } from "~/common/mui";

import CheckIcon from "@mui/icons-material/Check";
import { SHORTCUT_DEFINITIONS, getShortcutLabel, type ShortcutKey } from "./shortcuts";
import { DateFormatType, getPattern } from "~/common/utils/DateFormatter";

export interface AdvancedDateRangeValue {
  shortcut: ShortcutKey | null;
  dates: [Dayjs | null, Dayjs | null];
}

export interface AdvancedDateRangePickerProps {
  value?: AdvancedDateRangeValue;
  onChange?: (value: AdvancedDateRangeValue) => void;
  disabled?: boolean;
  label?: string;
  sx?: SxProps;
}

const OverlayWrapper = styled("div")({
  position: "relative",
  width: "100%",
});

const OverlayText = styled("span")(({ theme }) => ({
  position: "absolute",
  left: theme.spacing(1.75),
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  color: theme.palette.text.primary,
  fontSize: theme.typography.bodyLarge.fontSize,
  lineHeight: theme.typography.bodyLarge.lineHeight,
}));

const StyledShortcutsList = styled(List)(({ theme }) => ({
  padding: 0,
  minWidth: 160,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

type CustomTextFieldProps = React.ComponentPropsWithRef<typeof PickersTextField> & {
  showOverlay: boolean;
  overlayText: string;
  onCommit?: () => void;
};

const CustomTextField = ({ showOverlay, overlayText, onCommit, sx, onKeyDown, ...rest }: CustomTextFieldProps) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      onCommit?.();
    }
    onKeyDown?.(e);
  };

  return (
    <OverlayWrapper>
      <PickersTextField
        {...rest}
        onKeyDown={handleKeyDown}
        sx={[
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          showOverlay && {
            "& .MuiPickersSectionList-root": { color: "transparent" },
          },
        ]}
      />
      {showOverlay && <OverlayText>{overlayText}</OverlayText>}
    </OverlayWrapper>
  );
};

interface ShortcutsPanelProps extends PickersShortcutsProps<DateRange<Dayjs>> {
  selectedShortcut?: ShortcutKey | null;
}

const ShortcutsPanel = ({
  items = [],
  selectedShortcut,
  changeImportance = "accept",
  ...rest
}: ShortcutsPanelProps) => {
  const { setValue } = usePickerContext<PickerRangeValue>();

  const handleClick = (item: PickersShortcutsItem<DateRange<Dayjs>>) => {
    const newValue = item.getValue({ isValid: () => true });
    setValue(newValue as PickerRangeValue, {
      changeImportance,
      shortcut: { label: item.label, id: item.id },
    });
  };

  return (
    <StyledShortcutsList {...rest}>
      {items.map((item) => (
        <StyledListItemButton
          key={item.id ?? item.label}
          selected={selectedShortcut === item.id}
          onClick={() => handleClick(item)}
        >
          <CheckIcon fontSize="small" sx={{ mr: 1, visibility: selectedShortcut !== item.id ? "hidden" : undefined }} />
          <ListItemText primary={item.label} />
        </StyledListItemButton>
      ))}
    </StyledShortcutsList>
  );
};

const defaultValue: AdvancedDateRangeValue = { shortcut: null, dates: [null, null] };

export const AdvancedDateRangePicker = ({
  sx,
  value = defaultValue,
  onChange,
  disabled = false,
  label: _label,
}: AdvancedDateRangePickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [internalValue, setInternalValue] = useState<AdvancedDateRangeValue>(value);

  // Tracks the last value that was actually committed to onChange.
  // Used to avoid emitting duplicate events when blur fires after an already-committed change.
  const committedValueRef = useRef<AdvancedDateRangeValue>(value);

  useEffect(() => {
    setInternalValue(value);
    committedValueRef.current = value;
  }, [value]);

  const handleFocus = useCallback((_e: FocusEvent<HTMLDivElement>) => setFocused(true), []);

  // Commit the current draft to the parent when the user leaves the field.
  const handleBlur = useCallback(
    (_e: FocusEvent<HTMLDivElement>) => {
      setFocused(false);
      setInternalValue((draft) => {
        const committed = committedValueRef.current;
        const startSame =
          (draft.dates[0] === null && committed.dates[0] === null) ||
          (draft.dates[0] !== null && committed.dates[0] !== null && draft.dates[0].isSame(committed.dates[0], "day"));
        const endSame =
          (draft.dates[1] === null && committed.dates[1] === null) ||
          (draft.dates[1] !== null && committed.dates[1] !== null && draft.dates[1].isSame(committed.dates[1], "day"));

        if (!startSame || !endSame || draft.shortcut !== committed.shortcut) {
          committedValueRef.current = draft;
          onChange?.(draft);
        }
        return draft;
      });
    },
    [onChange],
  );

  // Commit on Enter key — wired through CustomTextField's onCommit prop.
  const handleCommit = useCallback(() => {
    setInternalValue((draft) => {
      const committed = committedValueRef.current;
      const startSame =
        (draft.dates[0] === null && committed.dates[0] === null) ||
        (draft.dates[0] !== null && committed.dates[0] !== null && draft.dates[0].isSame(committed.dates[0], "day"));
      const endSame =
        (draft.dates[1] === null && committed.dates[1] === null) ||
        (draft.dates[1] !== null && committed.dates[1] !== null && draft.dates[1].isSame(committed.dates[1], "day"));

      if (!startSame || !endSame || draft.shortcut !== committed.shortcut) {
        committedValueRef.current = draft;
        onChange?.(draft);
      }
      return draft;
    });
  }, [onChange]);

  const showOverlay = !focused && !open && value.shortcut !== "custom" && value.shortcut !== null;
  const overlayText = value.shortcut ? getShortcutLabel(value.shortcut, t) : "";

  const handleDateChange = (
    newDates: DateRange<Dayjs>,
    context: PickerChangeHandlerContext<DateRangeValidationError>,
  ) => {
    const isShortcut = !!context.shortcut;
    const shortcutKey = (context.shortcut?.id as ShortcutKey) ?? "custom";

    let resolvedDates: [Dayjs | null, Dayjs | null] = [newDates[0] ?? null, newDates[1] ?? null];

    if (!isShortcut) {
      const oldStart = internalValue.dates[0];
      const oldEnd = internalValue.dates[1];
      const newStart = newDates[0] ?? null;

      const startChanged =
        (newStart === null && oldStart !== null) ||
        (newStart !== null && (oldStart === null || !newStart.isSame(oldStart, "day")));

      // Only clear end date when the new start is strictly after the existing end.
      if (startChanged && newStart !== null && oldEnd !== null && newStart.isAfter(oldEnd, "day")) {
        resolvedDates = [newStart, null];
      }
    }

    const nextValue: AdvancedDateRangeValue = {
      shortcut: shortcutKey,
      dates: resolvedDates,
    };

    setInternalValue(nextValue);

    // Shortcuts and calendar selections (both dates filled) commit immediately.
    // Plain text input only updates the draft; commit happens on blur or Enter.
    if (isShortcut || (open && resolvedDates[0] && resolvedDates[1])) {
      committedValueRef.current = nextValue;
      onChange?.(nextValue);
    }
  };

  const shortcutItems: PickersShortcutsItem<DateRange<Dayjs>>[] = useMemo(
    () =>
      SHORTCUT_DEFINITIONS.map((shortcut) => ({
        label: t(shortcut.labelKey),
        getValue: () => shortcut.getValue(),
        id: shortcut.key,
      })),
    [t],
  );

  const shortcutsSlotProps: ShortcutsPanelProps = {
    items: shortcutItems,
    selectedShortcut: internalValue.shortcut,
  };

  return (
    <DateRangePicker
      sx={sx}
      value={internalValue.dates}
      onChange={handleDateChange}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      format={getPattern(DateFormatType.DATE)}
      disabled={disabled}
      calendars={2}
      displayWeekNumber
      slots={{
        shortcuts: ShortcutsPanel,
        textField: CustomTextField,
      }}
      localeText={{
        calendarWeekNumberText: (weekNumber) => t("common.calendar.calendarWeek", { weekNumber }),
        toolbarTitle: t("common.component.datePicker.title"),
      }}
      slotProps={{
        toolbar: {
          hidden: false,
          toolbarFormat: getPattern(DateFormatType.DATE),
        },
        popper: {
          placement: "bottom-end",
          sx: (theme) => ({
            "& .MuiPaper-root": {
              backgroundColor: theme.palette.surfaceContainer.low,
              boxShadow: theme.shadows[2],
            },
          }),
        },
        shortcuts: shortcutsSlotProps,
        textField: {
          showOverlay,
          overlayText,
          onFocus: handleFocus,
          onBlur: handleBlur,
          onCommit: handleCommit,
        } as Partial<CustomTextFieldProps>,
      }}
    />
  );
};
